import { useEffect, useState } from "react";
import {
    Briefcase,
    HelpCircle,
    Quote,
    Plus,
    Trash2,
    X,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// ClientShareablesPanel — cockpit-side editor for the three
// curated cards rendered inside the client space.
// 1. Expertises mobilisées  (whitelist)
// 2. Questions stratégiques à l'étude
// 3. Réflexions stratégiques XV
// ─────────────────────────────────────────────────────────────

export function ClientShareablesPanel({ dossier, onDossierUpdate }) {
    const dossierId = dossier.id;
    const [options, setOptions] = useState([]);
    const [questions, setQuestions] = useState(dossier.client_questions || []);
    const [newQuestion, setNewQuestion] = useState("");
    const [newReflection, setNewReflection] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const expertises = dossier.client_expertises || [];
    const reflections = dossier.client_reflections || [];

    useEffect(() => {
        cockpit
            .get("/client-expertise-options")
            .then(({ data }) => setOptions(data.options || []))
            .catch(() => setOptions([]));
    }, []);

    useEffect(() => {
        setQuestions(dossier.client_questions || []);
    }, [dossier.id, dossier.client_questions]);

    const flash = (m) => {
        setMsg(m);
        setTimeout(() => setMsg(""), 2400);
    };

    const toggleExpertise = async (opt) => {
        const next = expertises.includes(opt)
            ? expertises.filter((v) => v !== opt)
            : [...expertises, opt];
        try {
            setBusy(true);
            const { data } = await cockpit.put(
                `/dossiers/${dossierId}/client-expertises`,
                { expertises: next },
            );
            onDossierUpdate?.(data);
            flash("Expertises mises à jour ✓");
        } catch (e) {
            flash(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const saveQuestions = async (nextList) => {
        try {
            setBusy(true);
            const { data } = await cockpit.put(
                `/dossiers/${dossierId}/client-questions`,
                { questions: nextList },
            );
            onDossierUpdate?.(data);
            flash("Questions mises à jour ✓");
        } catch (e) {
            flash(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const addQuestion = () => {
        const v = newQuestion.trim();
        if (!v) return;
        const next = [...questions, v];
        setQuestions(next);
        setNewQuestion("");
        saveQuestions(next);
    };

    const removeQuestion = (idx) => {
        const next = questions.filter((_, i) => i !== idx);
        setQuestions(next);
        saveQuestions(next);
    };

    const addReflection = async () => {
        const v = newReflection.trim();
        if (!v) return;
        try {
            setBusy(true);
            const { data } = await cockpit.post(
                `/dossiers/${dossierId}/client-reflections`,
                { body: v },
            );
            onDossierUpdate?.(data);
            setNewReflection("");
            flash("Réflexion publiée ✓");
        } catch (e) {
            flash(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const removeReflection = async (rid) => {
        if (!window.confirm("Retirer cette réflexion de l'espace client ?")) return;
        try {
            setBusy(true);
            const { data } = await cockpit.delete(
                `/dossiers/${dossierId}/client-reflections/${rid}`,
            );
            onDossierUpdate?.(data);
            flash("Réflexion retirée");
        } catch (e) {
            flash(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            data-testid="cockpit-client-shareables"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="mb-10">
                <div className="overline text-[#d4af37]/70 mb-3">
                    Espace client · contenu partagé
                </div>
                <h2
                    className="font-serif text-[26px] sm:text-[30px] text-[#f4ecd8] leading-tight"
                    style={{ fontWeight: 400 }}
                >
                    Vitrine du conseil stratégique
                </h2>
                <p className="mt-2 text-[12.5px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                    Trois cartes visibles côté client. Aucun nom de partenaire
                    n'est exposé. L'objectif est de matérialiser la profondeur
                    du dispositif XV sans révéler les intervenants.
                </p>
                {msg && (
                    <div
                        data-testid="shareables-msg"
                        className="mt-4 text-[12px] text-[#d4af37]/85 border-l-2 border-[#d4af37] pl-3 py-1 italic"
                    >
                        {msg}
                    </div>
                )}
            </div>

            <div className="grid lg:grid-cols-12 gap-10">
                {/* 1. EXPERTISES MOBILISÉES */}
                <div className="lg:col-span-5">
                    <CardHeader
                        Icon={Briefcase}
                        label="Expertises mobilisées"
                        sub="Cochez les expertises réellement coordonnées sur le dossier."
                    />
                    <ul className="space-y-1.5">
                        {options.map((opt) => {
                            const active = expertises.includes(opt);
                            return (
                                <li key={opt}>
                                    <label
                                        data-testid={`expertise-${opt}`}
                                        className="flex items-center gap-3 py-2 px-1 cursor-pointer group"
                                    >
                                        <span
                                            className={`w-4 h-4 border flex items-center justify-center transition-colors duration-300 ${
                                                active
                                                    ? "border-[#d4af37] bg-[#d4af37]/20"
                                                    : "border-white/20 group-hover:border-[#d4af37]/50"
                                            }`}
                                        >
                                            {active && (
                                                <span className="block w-1.5 h-1.5 bg-[#d4af37]" />
                                            )}
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={active}
                                            disabled={busy}
                                            onChange={() => toggleExpertise(opt)}
                                            className="sr-only"
                                        />
                                        <span
                                            className={`font-serif text-[14.5px] ${
                                                active
                                                    ? "text-[#f4ecd8]"
                                                    : "text-[#f4ecd8]/70 group-hover:text-[#f4ecd8]/90"
                                            }`}
                                        >
                                            {opt}
                                        </span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* 2. QUESTIONS STRATÉGIQUES */}
                <div className="lg:col-span-7 space-y-12">
                    <div>
                        <CardHeader
                            Icon={HelpCircle}
                            label="Questions stratégiques à l'étude"
                            sub="Réflexions actuellement menées par le cabinet, visibles par le client."
                        />
                        <ul className="space-y-2 mb-5">
                            {questions.map((q, idx) => (
                                <li
                                    key={idx}
                                    data-testid={`question-${idx}`}
                                    className="group flex items-start gap-3 py-2.5 border-b border-white/[0.05] text-[14px] font-serif text-[#f4ecd8]/85 leading-[1.7]"
                                >
                                    <span className="text-[#d4af37]/70 mt-1.5 shrink-0">
                                        ·
                                    </span>
                                    <span className="flex-1">{q}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeQuestion(idx)}
                                        data-testid={`question-delete-${idx}`}
                                        className="opacity-0 group-hover:opacity-100 text-[#f4ecd8]/30 hover:text-[#e89090] transition mt-1.5"
                                    >
                                        <X strokeWidth={1.3} size={13} />
                                    </button>
                                </li>
                            ))}
                            {questions.length === 0 && (
                                <li className="text-[12px] text-[#f4ecd8]/35 italic font-light py-1">
                                    Aucune question publiée.
                                </li>
                            )}
                        </ul>
                        <div className="flex gap-2">
                            <input
                                data-testid="question-input"
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                                onKeyDown={(e) =>
                                    e.key === "Enter" &&
                                    (e.preventDefault(), addQuestion())
                                }
                                placeholder="Quel véhicule de structuration privilégier ?"
                                className="flex-1 bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[13.5px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
                            />
                            <button
                                type="button"
                                onClick={addQuestion}
                                data-testid="question-add"
                                disabled={busy}
                                className="text-[#d4af37]/80 hover:text-[#d4af37] transition px-2"
                            >
                                <Plus strokeWidth={1.5} size={16} />
                            </button>
                        </div>
                    </div>

                    {/* 3. RÉFLEXIONS STRATÉGIQUES XV */}
                    <div>
                        <CardHeader
                            Icon={Quote}
                            label="Réflexions stratégiques XV"
                            sub="Courtes notes signées XV Capital Partners, datées et visibles côté client."
                        />
                        <textarea
                            data-testid="reflection-input"
                            value={newReflection}
                            onChange={(e) => setNewReflection(e.target.value)}
                            rows={3}
                            placeholder="Une approche patrimoniale semble aujourd'hui plus pertinente qu'une logique exclusivement financière."
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-4 text-[14px] leading-[1.8] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 resize-y"
                        />
                        <div className="flex justify-end mt-3">
                            <button
                                type="button"
                                onClick={addReflection}
                                data-testid="reflection-add"
                                disabled={busy || !newReflection.trim()}
                                className="inline-flex items-center gap-3 px-5 py-2.5 text-[10px] tracking-[0.3em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <Plus strokeWidth={1.3} size={13} />
                                Publier
                            </button>
                        </div>

                        <ul className="mt-6 space-y-5">
                            {reflections.map((r) => (
                                <li
                                    key={r.id}
                                    data-testid={`reflection-${r.id}`}
                                    className="group border-l border-[#d4af37]/35 pl-5 py-1 relative"
                                >
                                    <p className="font-serif text-[14.5px] leading-[1.85] text-[#f4ecd8]/90 italic">
                                        « {r.body} »
                                    </p>
                                    <div className="mt-2 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/65">
                                        <span>
                                            {r.created_at &&
                                                new Date(
                                                    r.created_at,
                                                ).toLocaleDateString("fr-FR", {
                                                    day: "2-digit",
                                                    month: "long",
                                                    year: "numeric",
                                                })}
                                        </span>
                                        <span className="text-[#f4ecd8]/45">
                                            XV Capital Partners
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeReflection(r.id)}
                                        data-testid={`reflection-delete-${r.id}`}
                                        className="absolute -right-1 top-0 opacity-0 group-hover:opacity-100 text-[#f4ecd8]/30 hover:text-[#e89090] transition"
                                    >
                                        <Trash2 strokeWidth={1.2} size={13} />
                                    </button>
                                </li>
                            ))}
                            {reflections.length === 0 && (
                                <li className="text-[12px] text-[#f4ecd8]/35 italic font-light">
                                    Aucune réflexion publiée à ce jour.
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

function CardHeader({ Icon, label, sub }) {
    return (
        <div className="mb-5">
            <div className="overline text-[#d4af37]/70 mb-2">
                {Icon && (
                    <Icon
                        strokeWidth={1}
                        size={12}
                        className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                    />
                )}
                {label}
            </div>
            {sub && (
                <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light leading-relaxed">
                    {sub}
                </p>
            )}
        </div>
    );
}
